import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  X,
  ImagePlus,
  Loader2,
  Search,
  ChevronDown,
  ChevronRight,
  User,
  Users,
  MapPin,
  ShoppingBag,
  Trophy,
  CalendarDays,
  LucideIcon,
  Medal,
  Landmark,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import userService from '../../services/userService';
import postService, {
  DartPost,
  PostAuthorType,
  POST_AUTHOR_TYPE_LABELS,
  getImageUrl,
} from '../../services/postService';
import { apiErrorMessage } from '../../services/api';
import { t } from '../../i18n';

const ACCENT = '#3fced1';
const MAX_IMAGES = 10;
const MAX_CAPTION = 2200;
/** Gruppen mit mehr Einträgen starten im Auswahl-Panel eingeklappt. */
export const AUTHOR_GROUP_COLLAPSE_THRESHOLD = 8;

export interface ComposerAuthor {
  authorType: PostAuthorType;
  authorId: string;
  name?: string | null;
  /** Avatar: Profilbild des Users bzw. logo ?? image des Listings. */
  image?: string | null;
}

export interface AuthorOption extends ComposerAuthor {
  label: string;
}

export interface AuthorGroup {
  type: PostAuthorType;
  label: string;
  options: AuthorOption[];
}

/** Gruppen-Reihenfolge im Auswahl-Panel (wie in der App). */
export const AUTHOR_GROUP_ORDER: PostAuthorType[] = [
  'PLAYER',
  'CLUB',
  'PUB',
  'SHOP',
  'SERIES',
  'TOURNAMENT',
  'LEAGUE',
  'ASSOCIATION',
];

/** Gruppen-Überschriften (Plural, deutsch). */
export const AUTHOR_GROUP_LABELS: Record<PostAuthorType, string> = {
  PLAYER: 'Spielerprofil',
  CLUB: 'Vereine',
  PUB: t('web.component.footer.spielstaetten'),
  SHOP: 'Shops',
  SERIES: 'Turnierserien',
  TOURNAMENT: 'Turniere',
  LEAGUE: 'Ligen',
  ASSOCIATION: t('web.component.footer.verbaende'),
};

/** Fallback-Icons pro Absender-Typ (rund, #3fced1 auf #3fced1/15). */
const AUTHOR_TYPE_ICONS: Record<PostAuthorType, LucideIcon> = {
  PLAYER: User,
  CLUB: Users,
  PUB: MapPin,
  SHOP: ShoppingBag,
  TOURNAMENT: Trophy,
  SERIES: CalendarDays,
  LEAGUE: Medal,
  ASSOCIATION: Landmark,
};

/**
 * PURE: Absender-Optionen nach Typ gruppieren (feste Reihenfolge:
 * Spielerprofil, Vereine, Spielstätten, Shops, Turnierserien, Turniere)
 * und optional per Suchtext filtern. Leere Gruppen fallen weg.
 */
export function groupAuthorOptions(options: AuthorOption[], query: string): AuthorGroup[] {
  const q = (query ?? '').trim().toLowerCase();
  const source = Array.isArray(options) ? options : [];
  const filtered = q
    ? source.filter((o) => (o?.name ?? o?.label ?? '').toLowerCase().includes(q))
    : source;
  return AUTHOR_GROUP_ORDER.map((type) => ({
    type,
    label: AUTHOR_GROUP_LABELS[type],
    options: filtered.filter((o) => o?.authorType === type),
  })).filter((g) => g.options.length > 0);
}

interface PostComposerProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated?: (post: DartPost) => void;
  /** Absender vorbelegen (z.B. auf Detailseiten von Verein/Shop/…). */
  defaultAuthor?: ComposerAuthor | null;
}

function getEntityId(e: any): string | null {
  return (e?.id || e?._id) ?? null;
}

/** Datei → base64-Data-URL. */
function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

/** Runder Avatar einer Absender-Option: Bild oder Typ-Icon als Fallback. */
const AuthorAvatar: React.FC<{ option: ComposerAuthor; sizeClass?: string }> = ({
  option,
  sizeClass = 'h-8 w-8',
}) => {
  const url = getImageUrl(option.image ?? null);
  const Icon = AUTHOR_TYPE_ICONS[option.authorType] ?? User;
  if (url) {
    return (
      <img
        src={url}
        alt=""
        className={`${sizeClass} rounded-full object-cover shrink-0 bg-zinc-800`}
      />
    );
  }
  return (
    <span
      className={`${sizeClass} rounded-full flex items-center justify-center shrink-0`}
      style={{ backgroundColor: 'rgba(63,206,209,0.15)' }}
    >
      <Icon size={15} style={{ color: ACCENT }} />
    </span>
  );
};

/**
 * Vollflächen-Dialog zum Erstellen eines Beitrags: Absender-Auswahl über ein
 * eigenes Panel (Suchfeld, nach Typ gruppiert, große Gruppen eingeklappt,
 * runde Avatare), Multi-Bild-Upload (min 1, max 10, Vorschau 4:5) und
 * Caption. Ohne Login → Redirect zu /login mit Rücksprung (Muster
 * ListingFollowButton). Flaches Design (fixed inset-0 über allem,
 * App-Style-Kopfzeile).
 */
const PostComposer: React.FC<PostComposerProps> = ({ isOpen, onClose, onCreated, defaultAuthor = null }) => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [authors, setAuthors] = useState<AuthorOption[]>([]);
  const [authorsLoading, setAuthorsLoading] = useState(false);
  const [selectedAuthorKey, setSelectedAuthorKey] = useState<string>('');

  // Absender-Auswahl-Panel
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerQuery, setPickerQuery] = useState('');
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});

  const [images, setImages] = useState<string[]>([]);
  const [caption, setCaption] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const authorKey = (a: ComposerAuthor) => `${a.authorType}:${a.authorId}`;

  // Absender laden: "Ich" + verwaltete Einträge (Muster EntriesManager/ProfilePage)
  const loadAuthors = useCallback(async () => {
    setAuthorsLoading(true);
    const options: AuthorOption[] = [];
    const myId = (user as any)?.id ?? null;
    if (myId) {
      options.push({
        authorType: 'PLAYER',
        authorId: myId,
        name: user?.username ?? null,
        image: (user as any)?.profileImage ?? null,
        label: `Ich (${user?.username ?? 'Spieler'})`,
      });
    }
    try {
      const [oC, oP, oSh, oT, oSe, aC, aP, aSh, aT, aSe, oL, oA, aL, aA] = await Promise.all([
        userService.getOwnedClubs(),
        userService.getOwnedPubs(),
        userService.getOwnedShops(),
        userService.getOwnedTournaments(),
        userService.getOwnedSeries(),
        userService.getAdminClubs(),
        userService.getAdminPubs(),
        userService.getAdminShops(),
        userService.getAdminTournaments(),
        userService.getAdminSeries(),
        userService.getOwnedLeagues(),
        userService.getOwnedAssociations(),
        userService.getAdminLeagues(),
        userService.getAdminAssociations(),
      ]);
      const push = (list: any[], type: PostAuthorType) => {
        (Array.isArray(list) ? list : []).forEach((e) => {
          const id = getEntityId(e);
          if (!id) return;
          const key = `${type}:${id}`;
          if (options.some((o) => authorKey(o) === key)) return;
          options.push({
            authorType: type,
            authorId: id,
            name: e?.name ?? null,
            image: e?.logo ?? e?.image ?? null,
            label: `${e?.name ?? 'Ohne Namen'} (${POST_AUTHOR_TYPE_LABELS[type]})`,
          });
        });
      };
      push(oC, 'CLUB');
      push(aC, 'CLUB');
      push(oP, 'PUB');
      push(aP, 'PUB');
      push(oSh, 'SHOP');
      push(aSh, 'SHOP');
      push(oT, 'TOURNAMENT');
      push(aT, 'TOURNAMENT');
      push(oSe, 'SERIES');
      push(aSe, 'SERIES');
      push(oL, 'LEAGUE');
      push(aL, 'LEAGUE');
      push(oA, 'ASSOCIATION');
      push(aA, 'ASSOCIATION');
    } catch (err) {
      console.error('Fehler beim Laden der verwalteten Einträge:', err);
    }
    // Vorbelegten Absender ergänzen, falls er nicht in den geladenen Listen steckt
    if (defaultAuthor?.authorId) {
      const key = authorKey(defaultAuthor);
      if (!options.some((o) => authorKey(o) === key)) {
        options.push({
          ...defaultAuthor,
          label: `${defaultAuthor.name ?? 'Ohne Namen'} (${POST_AUTHOR_TYPE_LABELS[defaultAuthor.authorType]})`,
        });
      }
    }
    setAuthors(options);
    setSelectedAuthorKey((prev) => {
      if (prev && options.some((o) => authorKey(o) === prev)) return prev;
      if (defaultAuthor?.authorId) return authorKey(defaultAuthor);
      return options.length > 0 ? authorKey(options[0]) : '';
    });
    setAuthorsLoading(false);
  }, [user, defaultAuthor]);

  useEffect(() => {
    if (!isOpen) return;
    // Ohne Login ist der Composer nicht nutzbar → Login mit Rücksprung
    // (Muster ListingFollowButton)
    if (!isAuthenticated) {
      onClose();
      navigate('/login', { state: { from: { pathname: location.pathname } } });
      return;
    }
    loadAuthors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isAuthenticated]);

  // Scroll-Lock + ESC schließen
  useEffect(() => {
    if (!isOpen) return;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [isOpen, onClose]);

  const selectedAuthor = useMemo(
    () => authors.find((o) => authorKey(o) === selectedAuthorKey) ?? null,
    [authors, selectedAuthorKey]
  );

  const groups = useMemo(() => groupAuthorOptions(authors, pickerQuery), [authors, pickerQuery]);
  const searchActive = pickerQuery.trim().length > 0;

  if (!isOpen || !isAuthenticated) return null;

  const openPicker = () => {
    setPickerQuery('');
    // Gruppen mit mehr als 8 Einträgen starten eingeklappt
    const collapsed: Record<string, boolean> = {};
    groupAuthorOptions(authors, '').forEach((g) => {
      collapsed[g.type] = g.options.length > AUTHOR_GROUP_COLLAPSE_THRESHOLD;
    });
    setCollapsedGroups(collapsed);
    setPickerOpen(true);
  };

  const toggleGroup = (type: PostAuthorType) => {
    setCollapsedGroups((prev) => ({ ...prev, [type]: !prev[type] }));
  };

  const selectAuthor = (option: AuthorOption) => {
    setSelectedAuthorKey(authorKey(option));
    setPickerOpen(false);
  };

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setError(null);
    const remaining = MAX_IMAGES - images.length;
    if (remaining <= 0) {
      setError(t('web.component.post-composer.maximal-bilder-pro-beitrag', { MAX_IMAGES }));
      return;
    }
    const selected = Array.from(files).slice(0, remaining);
    if (files.length > remaining) {
      setError(t('web.component.post-composer.maximal-bilder-pro-beitrag-es', { MAX_IMAGES, remaining }));
    }
    try {
      const dataUrls = await Promise.all(selected.map(readFileAsDataUrl));
      setImages((prev) => [...prev, ...dataUrls.filter((d) => d.startsWith('data:'))].slice(0, MAX_IMAGES));
    } catch {
      setError(t('web.component.post-composer.bild-konnte-nicht-gelesen-werden'));
    }
  };

  const removeImage = (idx: number) => {
    setImages((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async () => {
    const author = selectedAuthor;
    if (!author) {
      setError(t('web.component.post-composer.bitte-waehle-einen-absender-aus'));
      return;
    }
    if (images.length === 0) {
      setError(t('web.component.post-composer.bitte-fuege-mindestens-ein-bild'));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const created = await postService.createPost({
        authorType: author.authorType,
        authorId: author.authorId,
        images,
        caption: caption.trim() || undefined,
      });
      if (created) onCreated?.(created);
      setImages([]);
      setCaption('');
      onClose();
    } catch (err: any) {
      setError(apiErrorMessage(err, t('web.component.post-composer.beitrag-konnte-nicht-erstellt-werden')));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-zinc-950 overflow-y-auto" role="dialog" aria-label={t('web.component.post-composer.beitrag-erstellen')}>
      {/* App-Style-Kopfzeile (Vollbild über allem) */}
      <div className="sticky top-0 z-10 bg-zinc-950/95 backdrop-blur border-b border-zinc-800">
        <div className="max-w-[600px] mx-auto px-4 py-3 flex items-center gap-3">
          <button
            type="button"
            onClick={onClose}
            aria-label={t('web.component.create-entry-modal.schliessen')}
            className="p-2 -ml-2 rounded-full text-zinc-300 hover:text-white hover:bg-zinc-800 transition"
          >
            <X size={20} />
          </button>
          <h1 className="text-base font-bold text-white flex-1">{t('web.component.post-composer.beitrag-erstellen')}</h1>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting || images.length === 0}
            className="px-4 py-2 rounded-xl text-sm font-semibold text-black disabled:opacity-40 transition"
            style={{ backgroundColor: ACCENT }}
          >
            {submitting ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 size={15} className="animate-spin" />
                {t('web.component.post-composer.posten')}
              </span>
            ) : (
              t('web.component.post-composer.posten-2')
            )}
          </button>
        </div>
      </div>

      <div className="max-w-[600px] mx-auto px-4 py-6 space-y-6">
        {/* Absender */}
        <div>
          <p className="block text-sm font-medium text-zinc-300 mb-1.5">{t('web.component.post-composer.posten-als')}</p>
          {authorsLoading ? (
            <div className="flex items-center gap-2 text-sm text-zinc-500 py-2">
              <Loader2 size={15} className="animate-spin" />
              {t('web.component.post-composer.absender-werden-geladen')}
            </div>
          ) : (
            <div className="relative">
              {/* Geschlossenes Auswahlfeld: Avatar + Label des gewählten Absenders */}
              <button
                type="button"
                onClick={openPicker}
                aria-label={t('web.component.post-composer.absender-waehlen')}
                aria-expanded={pickerOpen}
                className="w-full flex items-center gap-2.5 rounded-xl bg-zinc-900 border border-zinc-800 text-white text-sm px-3 py-2 text-left hover:border-zinc-600 focus:outline-none focus:border-[#3fced1] transition"
              >
                {selectedAuthor ? (
                  <>
                    <AuthorAvatar option={selectedAuthor} />
                    <span className="flex-1 truncate">{selectedAuthor.label}</span>
                  </>
                ) : (
                  <span className="flex-1 text-zinc-500">{t('web.component.post-composer.absender-waehlen-2')}</span>
                )}
                <ChevronDown size={16} className="text-zinc-500 shrink-0" />
              </button>

              {/* Auswahl-Panel: Suche + gruppierte Liste */}
              {pickerOpen && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setPickerOpen(false)} />
                  <div
                    className="absolute left-0 right-0 top-full mt-2 z-20 rounded-xl border border-zinc-800 bg-zinc-900 shadow-xl overflow-hidden"
                    data-testid="author-picker"
                  >
                    {/* Suchfeld oben */}
                    <div className="p-2 border-b border-zinc-800">
                      <div className="flex items-center gap-2 rounded-lg bg-zinc-950 border border-zinc-800 px-2.5 py-2 focus-within:border-[#3fced1]">
                        <Search size={14} className="text-zinc-500 shrink-0" />
                        <input
                          type="text"
                          value={pickerQuery}
                          onChange={(e) => setPickerQuery(e.target.value)}
                          placeholder={t('web.component.post-composer.absender-suchen')}
                          autoFocus
                          className="w-full bg-transparent text-sm text-white placeholder:text-zinc-600 focus:outline-none"
                        />
                      </div>
                    </div>

                    <div className="max-h-80 overflow-y-auto py-1">
                      {groups.length === 0 && (
                        <p className="px-4 py-3 text-sm text-zinc-500">{t('web.component.post-composer.keine-absender-gefunden')}</p>
                      )}
                      {groups.map((group) => {
                        // Bei aktiver Suche sind alle Gruppen offen
                        const collapsed = !searchActive && collapsedGroups[group.type] === true;
                        return (
                          <div key={group.type}>
                            {/* Gruppen-Kopf inkl. Anzahl */}
                            <button
                              type="button"
                              onClick={() => toggleGroup(group.type)}
                              className="w-full flex items-center justify-between gap-2 px-3.5 py-2 text-[11px] font-semibold uppercase tracking-wide text-zinc-500 hover:text-zinc-300 transition"
                            >
                              <span>
                                {group.label}
                                <span className="ml-1.5 font-medium normal-case">({group.options.length})</span>
                              </span>
                              {collapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
                            </button>
                            {!collapsed &&
                              group.options.map((option) => {
                                const isSelected = authorKey(option) === selectedAuthorKey;
                                return (
                                  <button
                                    key={authorKey(option)}
                                    type="button"
                                    onClick={() => selectAuthor(option)}
                                    className={`w-full flex items-center gap-2.5 px-3.5 py-2 text-sm text-left transition hover:bg-zinc-800 ${
                                      isSelected ? 'text-[#3fced1]' : 'text-zinc-200'
                                    }`}
                                  >
                                    <AuthorAvatar option={option} />
                                    <span className="truncate">{option.label}</span>
                                  </button>
                                );
                              })}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Bilder */}
        <div>
          <p className="text-sm font-medium text-zinc-300 mb-1.5">
            {t('web.component.post-composer.bilder')}{' '}<span className="text-zinc-500">{t('web.component.post-composer.mindestens-1', { length: images.length, MAX_IMAGES })}</span>
          </p>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
            {images.map((img, idx) => (
              <div key={idx} className="relative aspect-[4/5] rounded-xl overflow-hidden bg-zinc-900">
                <img src={img} alt={t('web.component.post-composer.vorschau', { idx: idx + 1 })} className="w-full h-full object-cover" />
                <button
                  type="button"
                  onClick={() => removeImage(idx)}
                  aria-label={t('web.component.post-composer.bild-entfernen', { idx: idx + 1 })}
                  className="absolute top-1.5 right-1.5 p-1 rounded-full bg-black/70 text-white hover:bg-black/90 transition"
                >
                  <X size={14} />
                </button>
              </div>
            ))}
            {images.length < MAX_IMAGES && (
              <label className="aspect-[4/5] rounded-xl border border-dashed border-zinc-700 hover:border-[#3fced1] flex flex-col items-center justify-center gap-1.5 cursor-pointer text-zinc-500 hover:text-zinc-300 transition">
                <ImagePlus size={22} style={{ color: ACCENT }} />
                <span className="text-[11px] font-medium">{t('web.component.listing-admins-card.hinzufuegen')}</span>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  data-testid="post-image-input"
                  onChange={(e) => {
                    handleFiles(e.target.files);
                    e.target.value = '';
                  }}
                />
              </label>
            )}
          </div>
        </div>

        {/* Caption */}
        <div>
          <label htmlFor="post-caption" className="block text-sm font-medium text-zinc-300 mb-1.5">
            {t('web.component.post-composer.bildunterschrift')}{' '}<span className="text-zinc-500">(optional)</span>
          </label>
          <textarea
            id="post-caption"
            value={caption}
            maxLength={MAX_CAPTION}
            onChange={(e) => setCaption(e.target.value)}
            rows={4}
            placeholder={t('web.component.post-composer.schreibe-etwas-zu-deinem-beitrag')}
            className="w-full rounded-xl bg-zinc-900 border border-zinc-800 text-white text-sm px-3.5 py-2.5 resize-y focus:outline-none focus:border-[#3fced1]"
          />
          <p className="mt-1 text-right text-[11px] text-zinc-600">
            {caption.length}/{MAX_CAPTION}
          </p>
        </div>

        {error && <p className="text-sm text-red-400">{error}</p>}
      </div>
    </div>
  );
};

export default PostComposer;
